import React, { useState, useEffect, useCallback } from 'react';
import { Container, Row, Col, Form, Button, Spinner, Alert, Table } from 'react-bootstrap';
import { Bar } from 'react-chartjs-2';
import {
	Chart as ChartJS,
	CategoryScale,
	LinearScale,
	BarElement,
	Title,
	Tooltip,
	Legend,
} from 'chart.js';
import axiosClient from '../api/axiosClient';
import { API_BASE_URL } from '../config/api';

ChartJS.register(CategoryScale, LinearScale, BarElement, Title, Tooltip, Legend);

// Single hue per chart — funnel steps are one series of magnitudes, so the
// category axis carries identity and color carries nothing. Never rainbow.
const SERIES_RED = '#e34948';
const TEXT_PRIMARY = '#0b0b0b';
const TEXT_SECONDARY = '#52514e';
const GRID = '#e3e8ee';
// Drop-off severity. Only three steps, because a continuous gradient invites
// reading precision into a number that is already an estimate.
const DROP_BAD = '#c0392b';
const DROP_WARN = '#b9770e';
const DROP_OK = '#52514e';
const GAIN = '#1e7e46';

const cardStyle = {
	background: '#fff',
	borderRadius: '16px',
	border: '1px solid #e3e8ee',
	boxShadow: '0 2px 4px rgba(50,50,93,0.06)',
	padding: '1.5rem',
	marginBottom: '1.5rem',
};

const cardTitle = {
	fontSize: '0.8rem',
	fontWeight: 600,
	color: '#425466',
	textTransform: 'uppercase',
	letterSpacing: '0.08em',
	marginBottom: '0.25rem',
};

const cardSubtitle = {
	fontSize: '0.8rem',
	color: TEXT_SECONDARY,
	marginBottom: '1.25rem',
};

const fmtInt = (n) => (n === null || n === undefined ? '—' : Number(n).toLocaleString());
const fmtPct = (n) => (n === null || n === undefined ? '—' : `${(n * 100).toFixed(1)}%`);

// Retention: the share of the previous stage that carried through to this one.
// Over 100% is a real signal, not a glitch: the stages are not strictly
// sequential — checkout can be reached without submitting the visualizer, and
// the pricing entry never loads it — so a later stage can hold more people than
// the one before it. Rendering that as a green figure is honest; clamping it to
// 100% would hide the fact that the step is skippable.
const retentionColor = (kept) => {
	if (kept === null || kept === undefined) return TEXT_SECONDARY;
	if (kept > 1) return GAIN;
	if (kept <= 0.5) return DROP_BAD;
	if (kept <= 0.75) return DROP_WARN;
	return DROP_OK;
};

const fmtRetention = (kept) =>
	kept === null || kept === undefined ? '—' : `${(kept * 100).toFixed(1)}%`;

// Step names are long, so they wrap onto their own lines rather than being
// rotated — Chart.js renders an array label as stacked lines.
const wrapLabel = (label, maxChars = 12) => {
	const words = String(label).split(' ');
	const lines = [];
	let line = '';
	words.forEach((word) => {
		const candidate = line ? `${line} ${word}` : word;
		if (candidate.length > maxChars && line) {
			lines.push(line);
			line = word;
		} else {
			line = candidate;
		}
	});
	if (line) lines.push(line);
	return lines;
};

const verticalBarOptions = (maxValue, tooltipFormatter, titleFormatter) => ({
	responsive: true,
	maintainAspectRatio: false,
	layout: { padding: { top: 38 } },
	plugins: {
		legend: { display: false },
		tooltip: {
			backgroundColor: '#0b0b0b',
			padding: 10,
			displayColors: false,
			callbacks: { label: tooltipFormatter, title: titleFormatter },
		},
	},
	scales: {
		x: {
			grid: { display: false, drawBorder: false },
			ticks: { color: TEXT_PRIMARY, font: { size: 11 }, autoSkip: false, maxRotation: 0, minRotation: 0 },
		},
		y: {
			beginAtZero: true,
			suggestedMax: maxValue * 1.18,
			grid: { color: GRID, drawBorder: false },
			ticks: { color: TEXT_SECONDARY, font: { size: 11 } },
		},
	},
});

// Draws the count above each bar. Chart.js has no built-in data labels and
// chartjs-plugin-datalabels isn't a dependency, so this inline plugin does the
// one thing we need rather than pulling in a package.
const barValueLabels = {
	id: 'barValueLabels',
	afterDatasetsDraw(chart) {
		const { ctx } = chart;
		ctx.save();
		ctx.textAlign = 'center';
		ctx.textBaseline = 'bottom';
		chart.data.datasets.forEach((dataset, di) => {
			const meta = chart.getDatasetMeta(di);
			if (meta.hidden) return;
			meta.data.forEach((bar, i) => {
				const value = dataset.data[i];
				if (value === null || value === undefined) return;
				ctx.font = '600 12px system-ui, -apple-system, sans-serif';
				ctx.fillStyle = TEXT_PRIMARY;
				ctx.fillText(Number(value).toLocaleString(), bar.x, bar.y - 6);
			});
		});
		ctx.restore();
	},
};

function DeclineChart({ steps, color, height }) {
	const max = Math.max(...steps.map((s) => s.count), 1);
	const data = {
		labels: steps.map((s) => wrapLabel(s.label)),
		datasets: [
			{ data: steps.map((s) => s.count), backgroundColor: color, borderRadius: 4, barPercentage: 0.62, categoryPercentage: 0.82 },
		],
	};
	const options = verticalBarOptions(
		max,
		(ctx) => `${fmtInt(steps[ctx.dataIndex].count)} attempts`,
		(items) => steps[items[0].dataIndex].fullLabel || steps[items[0].dataIndex].label
	);
	return (
		<div style={{ height }}>
			<Bar data={data} options={options} plugins={[barValueLabels]} />
		</div>
	);
}

function StatTile({ label, value, hint }) {
	return (
		<div style={{ ...cardStyle, marginBottom: 0, height: '100%' }}>
			<div style={cardTitle}>{label}</div>
			<div style={{ fontSize: '2rem', fontWeight: 700, color: TEXT_PRIMARY, lineHeight: 1.1 }}>{value}</div>
			{hint && <div style={{ fontSize: '0.8rem', color: TEXT_SECONDARY, marginTop: '0.35rem' }}>{hint}</div>}
		</div>
	);
}

// The stage table. Columns alternate count / drop-to-next, the same shape the
// User Clickthrough Journey table uses, so the two read the same way. Rows are
// days, newest first, with the range total pinned on top — a single day is too
// small a sample to judge a step by, and the total is what a rate should be
// computed from.
function StageTable({ stages, daily, totals }) {
	const headerCell = {
		position: 'sticky',
		top: 0,
		background: '#f6f8fa',
		color: TEXT_SECONDARY,
		fontSize: '0.72rem',
		fontWeight: 600,
		textTransform: 'uppercase',
		letterSpacing: '0.04em',
		whiteSpace: 'nowrap',
		verticalAlign: 'bottom',
	};
	const dateCell = {
		position: 'sticky',
		left: 0,
		background: '#fff',
		fontWeight: 600,
		whiteSpace: 'nowrap',
		borderRight: `1px solid ${GRID}`,
	};

	const renderRow = (label, rowStages, opts = {}) => (
		<tr style={opts.emphasis ? { background: '#f6f8fa', fontWeight: 600 } : undefined}>
			<td style={{ ...dateCell, background: opts.emphasis ? '#f6f8fa' : '#fff' }}>{label}</td>
			{rowStages.map((s, i) => (
				<React.Fragment key={s.key}>
					<td className="text-end" style={{ whiteSpace: 'nowrap' }}>
						{fmtInt(s.count)}
					</td>
					{i < rowStages.length - 1 && (
						<td
							className="text-end"
							style={{
								whiteSpace: 'nowrap',
								color: retentionColor(rowStages[i + 1].stepCvr),
								fontVariantNumeric: 'tabular-nums',
								fontSize: '0.8rem',
							}}
							title={
								rowStages[i + 1].stepCvr === null
									? undefined
									: `${fmtRetention(rowStages[i + 1].stepCvr)} of ${s.label} carried through to ${
											rowStages[i + 1].label
									  } · ${fmtInt(rowStages[i + 1].dropCount)} lost`
							}
						>
							{fmtRetention(rowStages[i + 1].stepCvr)}
						</td>
					)}
				</React.Fragment>
			))}
		</tr>
	);

	return (
		<div style={{ overflowX: 'auto' }}>
			<Table hover size="sm" className="mb-0" style={{ fontSize: '0.85rem', minWidth: 1100 }}>
				<thead>
					<tr>
						<th style={{ ...headerCell, left: 0, zIndex: 2 }}>Date</th>
						{stages.map((s, i) => (
							<React.Fragment key={s.key}>
								<th className="text-end" style={headerCell} title={`Source: ${s.source}`}>
									{s.label}
								</th>
								{i < stages.length - 1 && (
									<th className="text-end" style={{ ...headerCell, fontWeight: 500 }}>
										kept
									</th>
								)}
							</React.Fragment>
						))}
					</tr>
				</thead>
				<tbody>
					{daily.map((d) => (
						<React.Fragment key={d.date}>{renderRow(d.date, d.stages)}</React.Fragment>
					))}
				</tbody>
				{/* In a tfoot so the total stays pinned under the days rather than
				    sorting among them, and reads as a summary of what is above it. */}
				{totals && totals.length > 0 && (
					<tfoot style={{ borderTop: `2px solid ${GRID}` }}>{renderRow('Range total', totals, { emphasis: true })}</tfoot>
				)}
			</Table>
		</div>
	);
}

// Dates are picked in America/Chicago, because that's the window the API buckets
// on. toISOString() would give the UTC date, so between 7pm and midnight Chicago
// "today" silently became tomorrow and the page opened on an empty range.
// en-CA formats as YYYY-MM-DD, which is exactly what the API expects.
// ─── Offer Testing tab ──────────────────────────────────────────────────────
// One row per arm of the live offer test (backend: utils/offerTest.js). Every
// column is unique people, so a reload cannot inflate an arm.
//
// Ranked on value per visitor, NOT conversion rate. A starter buyer is worth
// roughly a quarter of a pro buyer, so an arm can win on CVR and still lose
// money — which is precisely what the arm that hides the bottom tier tests.
// No arm is called until it has the minimum visitors; below that the table
// shows how many are still needed instead of a verdict.
function OfferTesting() {
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const load = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await axiosClient.get(`${API_BASE_URL}/admin/offer-test`);
			setData(res.data);
		} catch (err) {
			console.error('Error loading offer test:', err);
			setError('Failed to load offer test.');
		} finally {
			setLoading(false);
		}
	}, []);

	useEffect(() => { load(); }, [load]);

	const variants = data?.variants || [];
	const minVisitors = data?.minVisitors || 8000;
	const ready = variants.filter((v) => v.live && v.visitors >= minVisitors && v.profitPerVisitor !== null);
	const best = ready.slice().sort((a, b) => b.profitPerVisitor - a.profitPerVisitor)[0];
	const fmtMoney = (n, dp = 3) => (n === null || n === undefined ? '—' : `${n < 0 ? '−$' : '$'}${Math.abs(n).toFixed(dp)}`);

	return (
		<>
			<div style={{ ...cardStyle, padding: '1rem 1.5rem' }}>
				<Row className="align-items-center g-3">
					<Col xs={12} md>
						<div style={{ fontSize: '0.75rem', color: TEXT_SECONDARY, textTransform: 'uppercase', letterSpacing: '0.08em' }}>
							Splitting traffic from
						</div>
						<div style={{ fontFamily: 'monospace', fontSize: '1.05rem', fontWeight: 700, color: TEXT_PRIMARY, marginTop: '0.15rem' }}>
							{data?.ad || '—'}
						</div>
						<div style={{ fontSize: '0.78rem', color: TEXT_SECONDARY, marginTop: '0.25rem' }}>
							Only this ad is split, so creative is held constant and the offer is the
							only thing that differs. Every other ad is served the control and written
							to nothing. Counted from the day the test started — a rolling window would
							slide the early arms out of the comparison and the same test would read
							differently every day.
						</div>
						<div style={{ fontSize: '0.82rem', color: TEXT_PRIMARY, marginTop: '0.5rem' }}>
							<strong>{fmtInt(data?.adClicks)}</strong> landing clicks
							<span style={{ color: TEXT_SECONDARY }}>
								{' '}&rarr; <strong>{fmtInt(variants.reduce((n, v) => n + v.visitors, 0))}</strong> reached the plans page
								{data?.adClicks > 0 && (
									<> ({((variants.reduce((n, v) => n + v.visitors, 0) / data.adClicks) * 100).toFixed(1)}%)</>
								)}
							</span>
						</div>
					</Col>
					<Col xs={12} md="auto">
						<div style={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>Window</div>
						<div style={{ fontSize: '0.95rem', fontWeight: 600, color: TEXT_PRIMARY, marginTop: '0.15rem' }}>
							Lifetime
						</div>
						<div style={{ fontSize: '0.72rem', color: TEXT_SECONDARY }}>
							since {data?.since || '—'}, today included
						</div>
					</Col>
				</Row>
			</div>

			{error && <Alert variant="danger">{error}</Alert>}
			{loading && <div className="text-center py-5"><Spinner animation="border" /></div>}

			{!loading && data && (
				<>
					<div style={cardStyle}>
						<div style={cardTitle}>Arms</div>
						<div style={cardSubtitle}>
							Unique people at each step. The winner is the arm with the highest value per
							visitor once it has {fmtInt(minVisitors)} visitors — read that column, not CVR.
							A visitor is someone who reached the tier menu — that is where the arm is
							assigned and where the offer first differs. Clicks are each arm's equal share
							of the ad's landing clicks: the funnel before the plans page is identical for
							every arm, so they own that traffic equally by construction and the uneven
							visitor counts are randomisation noise, not reach.
							<strong> CVR / click</strong> is buyers over those clicks — the rate that lines
							up with Ads Manager. <strong>CVR / visitor</strong> is buyers over people who
							actually saw a tier menu, which is the cleaner read on the offer itself.
						</div>
						<div style={{ overflowX: 'auto' }}>
							<Table hover size="sm" className="mb-0" style={{ fontSize: '0.85rem', minWidth: 980 }}>
								<thead>
									<tr style={{ color: TEXT_SECONDARY, fontSize: '0.72rem', textTransform: 'uppercase', letterSpacing: '0.06em' }}>
										<th>Arm</th>
										<th className="text-end">Clicks</th>
										<th className="text-end">Visitors</th>
										<th className="text-end">CVR / click</th>
										<th className="text-end">Checkout</th>
										<th className="text-end">Submitted</th>
										<th className="text-end">Buyers</th>
										<th className="text-end">CVR / visitor</th>
										<th className="text-end">Packs s/b/p</th>
										<th className="text-end">Value/visitor</th>
										<th className="text-end">Profit/visitor</th>
										<th className="text-end">Status</th>
									</tr>
								</thead>
								<tbody style={{ fontVariantNumeric: 'tabular-nums' }}>
									{variants.map((v) => {
										const isBest = best && best.src === v.src;
										const short = minVisitors - v.visitors;
										return (
											<tr key={v.src} style={isBest ? { background: '#eaf4ec' } : undefined}>
												<td>
													<span style={{ fontFamily: 'monospace', fontWeight: 600 }}>{v.src}</span>
													{v.control && <span style={{ fontSize: '0.68rem', color: TEXT_SECONDARY }}> · control</span>}
													{!v.live && <span style={{ fontSize: '0.68rem', color: DROP_WARN }}> · not live</span>}
													<div style={{ fontSize: '0.74rem', color: TEXT_SECONDARY }}>{v.detail}</div>
												</td>
												<td className="text-end" style={{ color: TEXT_SECONDARY }}>{fmtInt(v.clicks)}</td>
												<td className="text-end">{fmtInt(v.visitors)}</td>
												<td className="text-end" style={{ fontWeight: 600 }}>
													{v.cvrOnClicks == null ? '—' : `${v.cvrOnClicks.toFixed(2)}%`}
												</td>
												<td className="text-end">{fmtInt(v.checkouts)}</td>
												<td className="text-end">{fmtInt(v.submits)}</td>
												<td className="text-end">{fmtInt(v.buyers)}</td>
												<td className="text-end">{v.cvr === null ? '—' : `${v.cvr.toFixed(2)}%`}</td>
												<td className="text-end" style={{ color: TEXT_SECONDARY }}>
													{v.packs.starter}/{v.packs.basic}/{v.packs.pro}
												</td>
												<td className="text-end">{fmtMoney(v.valuePerVisitor)}</td>
												<td className="text-end" style={{ fontWeight: 600, color: v.profitPerVisitor === null ? TEXT_SECONDARY : v.profitPerVisitor > 0 ? GAIN : DROP_BAD }}>
													{fmtMoney(v.profitPerVisitor)}
												</td>
												<td className="text-end" style={{ fontSize: '0.74rem' }}>
													{!v.live ? <span style={{ color: TEXT_SECONDARY }}>paused</span>
														: short > 0 ? <span style={{ color: DROP_WARN }}>need {fmtInt(short)} more</span>
														: isBest ? <span style={{ color: GAIN, fontWeight: 600 }}>winning</span>
														: <span style={{ color: TEXT_SECONDARY }}>read</span>}
												</td>
											</tr>
										);
									})}
								</tbody>
							</Table>
						</div>
					</div>

					<div style={cardStyle}>
						<div style={cardTitle}>How this is scored</div>
						<div style={{ fontSize: '0.82rem', color: TEXT_SECONDARY, lineHeight: 1.6 }}>
							<p className="mb-2">
								<strong>Profit per visitor</strong> = (buyers × what their packs are worth) ÷ visitors − CPC.
								A buyer is valued at day-one net plus three refill cycles:{' '}
								<code>starter {fmtMoney(data.packValue?.starter, 2)}</code>,{' '}
								<code>basic {fmtMoney(data.packValue?.basic, 2)}</code>,{' '}
								<code>pro {fmtMoney(data.packValue?.pro, 2)}</code>. Those are assumptions
								from /stats123's projection, not measured per arm — the ranking is firmer
								than the absolute figures.
							</p>
							<p className="mb-2">
								CPC is {fmtMoney(data.cpc, 3)}, the whole funnel's spend over its clicks.
								Spend is logged per day and account rather than per ad, so this test ad's
								own CPC will differ.
							</p>
							<p className="mb-0">
								Value per visitor is withheld for an arm whose purchase rows are missing a
								pack, rather than averaged over the ones that have it.
								{variants.some((v) => v.unpricedBuyers > 0) && (
									<strong> Some rows are missing a pack right now.</strong>
								)}
							</p>
						</div>
					</div>
				</>
			)}
		</>
	);
}

const CHICAGO_DATE = new Intl.DateTimeFormat('en-CA', {
	timeZone: 'America/Chicago',
	year: 'numeric',
	month: '2-digit',
	day: '2-digit',
});

const daysAgo = (n) => {
	const d = new Date();
	d.setDate(d.getDate() - n);
	return CHICAGO_DATE.format(d);
};

function Cro() {
	// Opens on today. A week costs ~7s to build and 30 days ~12s, because every
	// stage is counted per day across five tables — long enough that the page
	// reads as broken. Today is ~0.3s. The date pickers are right there for a
	// wider window, so the trend is one click away rather than the default.
	const [startDate, setStartDate] = useState(daysAgo(0));
	const [endDate, setEndDate] = useState(daysAgo(0));
	const [funnel, setFunnel] = useState('all');
	const [tab, setTab] = useState('offers');
	const [data, setData] = useState(null);
	const [loading, setLoading] = useState(true);
	const [error, setError] = useState(null);

	const fetchData = useCallback(async () => {
		setLoading(true);
		setError(null);
		try {
			const res = await axiosClient.get(`${API_BASE_URL}/admin/cro-funnel`, {
				params: { startDate, endDate, funnel },
			});
			setData(res.data);
		} catch (err) {
			console.error('Error loading CRO funnel:', err);
			setError('Failed to load funnel data.');
		} finally {
			setLoading(false);
		}
	}, [startDate, endDate, funnel]);

	// The funnel query costs a request even for a single day, and the page now
	// opens on Offer Testing — so it is fetched when that tab is first shown
	// rather than on mount, and not again once it has data.
	useEffect(() => {
		if (tab === 'funnel' && !data) fetchData();
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [tab]);

	const stages = data?.stages || [];
	const daily = data?.daily || [];
	const totals = data?.totals || [];
	const declines = data?.declines || [];

	const stageBy = (key) => totals.find((s) => s.key === key);
	const top = totals[0]?.count || 0;
	const paid = stageBy('paid')?.count || 0;
	const regs = stageBy('regs')?.count || 0;
	const checkoutStarted = stageBy('InitiateCheckout')?.count || 0;
	const submittedCard = stageBy('submitted_card')?.count || 0;
	// Any rate spanning the pixel half and the app half is only meaningful when
	// both cover the same population, which an entry-point filter breaks. The API
	// nulls those rates for the same reason; these tiles follow it rather than
	// recomputing a number the table deliberately refuses to show.
	// Optional chaining is load-bearing: this runs on the first render too, when
	// data is still null, and an unguarded read threw before the page could paint.
	const crossHalfOk = (data?.scope || 'all') === 'all';

	// The single worst step in the range — the one to go and fix. Skipped stages
	// (retention over 100%) are not candidates, and neither is the top of funnel,
	// which has nothing before it to retain from.
	const worst = totals
		.filter((s) => s.stepCvr !== null && s.stepCvr < 1)
		.sort((a, b) => a.stepCvr - b.stepCvr)[0];

	return (
		<div style={{ background: '#f6f9fc', minHeight: '100vh', padding: '2rem 0' }}>
			<Container fluid style={{ maxWidth: '1480px' }}>
				<h1 style={{ fontSize: '1.6rem', fontWeight: 700, color: TEXT_PRIMARY, marginBottom: '0.25rem' }}>
					One-Time Funnel
				</h1>
				<p style={{ color: TEXT_SECONDARY, marginBottom: '1.5rem' }}>
					Package buyers only — the 50off offer, excluding anyone who holds a subscription.
					Landing through paid, with the amount and share lost at every step.
				</p>

				<div style={{ display: 'flex', gap: '0.5rem', marginBottom: '1.25rem' }}>
					{[['funnel', 'Funnel'], ['offers', 'Offer Testing']].map(([key, label]) => (
						<Button
							key={key}
							size="sm"
							variant={tab === key ? 'primary' : 'outline-secondary'}
							onClick={() => setTab(key)}
						>
							{label}
						</Button>
					))}
				</div>

				{tab === 'offers' && <OfferTesting />}

				{tab === 'funnel' && (
				<>
				<div style={{ ...cardStyle, padding: '1rem 1.5rem' }}>
					<Form
						onSubmit={(e) => {
							e.preventDefault();
							fetchData();
						}}
					>
						<Row className="align-items-end g-3">
							<Col xs={12} md="auto">
								<Form.Label style={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>Start date</Form.Label>
								<Form.Control type="date" value={startDate} onChange={(e) => setStartDate(e.target.value)} size="sm" />
							</Col>
							<Col xs={12} md="auto">
								<Form.Label style={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>End date</Form.Label>
								<Form.Control type="date" value={endDate} onChange={(e) => setEndDate(e.target.value)} size="sm" />
							</Col>
							<Col xs={12} md="auto">
								<Form.Label style={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>Entry point</Form.Label>
								<Form.Select size="sm" value={funnel} onChange={(e) => setFunnel(e.target.value)}>
									<option value="all">All entries</option>
									<option value="50off">50off (visualizer)</option>
									<option value="50off-visualizer">50off-visualizer</option>
									<option value="50off-pricing">50off (pricing page)</option>
								</Form.Select>
							</Col>
							<Col xs={12} md="auto">
								<Button type="submit" size="sm" variant="primary" disabled={loading}>
									{loading ? 'Loading…' : 'Apply'}
								</Button>
							</Col>
							<Col xs={12} md="auto">
								{[
									{ label: 'Today', from: 0 },
									{ label: 'Last 7d', from: 6 },
									{ label: 'Last 14d', from: 13 },
									{ label: 'Last 30d', from: 29 },
								].map((preset) => (
									<Button
										key={preset.label}
										size="sm"
										variant="outline-secondary"
										className="me-2"
										onClick={() => {
											setStartDate(daysAgo(preset.from));
											setEndDate(daysAgo(0));
										}}
									>
										{preset.label}
									</Button>
								))}
							</Col>
						</Row>
					</Form>
				</div>

				{error && <Alert variant="danger">{error}</Alert>}

				{loading && (
					<div className="text-center py-5">
						<Spinner animation="border" />
					</div>
				)}

				{!loading && data && (
					<>
						{data.scopeNote && (
							<Alert variant="warning" style={{ fontSize: '0.85rem' }}>
								{data.scopeNote}
							</Alert>
						)}

						<Row className="g-3 mb-4">
							<Col xs={6} lg={3}>
								<StatTile
									label="Landing → Paid"
									value={fmtPct(crossHalfOk && top ? paid / top : null)}
									hint={
										crossHalfOk
											? `${fmtInt(paid)} of ${fmtInt(top)} landings`
											: 'Not comparable for a single entry point'
									}
								/>
							</Col>
							<Col xs={6} lg={3}>
								<StatTile
									label="Reg → Paid"
									value={fmtPct(regs ? paid / regs : null)}
									hint={`${fmtInt(paid)} of ${fmtInt(regs)} registrations`}
								/>
							</Col>
							<Col xs={6} lg={3}>
								<StatTile
									label="Checkout → Card Entered"
									value={fmtPct(crossHalfOk && checkoutStarted ? submittedCard / checkoutStarted : null)}
									hint={
										crossHalfOk
											? `${fmtInt(submittedCard)} of ${fmtInt(checkoutStarted)} reached checkout`
											: 'Not comparable for a single entry point'
									}
								/>
							</Col>
							<Col xs={6} lg={3}>
								<StatTile
									label="Weakest Step"
									value={worst ? fmtRetention(worst.stepCvr) : '—'}
									hint={worst ? `kept into ${worst.label} · ${fmtInt(worst.dropCount)} lost` : 'No loss in range'}
								/>
							</Col>
						</Row>

						<div style={cardStyle}>
							<div style={cardTitle}>Stage by Stage</div>
							<div style={cardSubtitle}>
								Each pair of columns is a stage count and the share of it kept reaching the
								next stage. Newest day first; the range total at the bottom is computed
								from summed counts, not by averaging the daily rates.
							</div>
							<StageTable stages={stages} daily={daily} totals={totals} />
						</div>

						<div style={cardStyle}>
							<div style={cardTitle}>Decline Reasons</div>
							<div style={cardSubtitle}>Why cards that were submitted did not go through.</div>
							{declines.length === 0 ? (
								<p style={{ color: TEXT_SECONDARY, marginBottom: 0 }}>No failed attempts in this range.</p>
							) : (
								<DeclineChart
									steps={declines.map((d, i) => ({
										key: `decline-${i}`,
										// Decline reasons are full sentences; vertical bars have one
										// column of width each, so they're clipped to fit.
										label: d.reason.length > 28 ? `${d.reason.slice(0, 27)}…` : d.reason,
										fullLabel: d.reason,
										count: d.count,
									}))}
									color={SERIES_RED}
									height={340}
								/>
							)}
						</div>

						<div style={{ fontSize: '0.75rem', color: TEXT_SECONDARY }}>
							<p>
								The first five stages are counted from <code>fbclids</code> (distinct click
								IDs per Meta event) because nothing is written to our own tables until an
								account exists. The last four are counted from the database, against the
								day each account registered. The two do not agree and are not meant to: a
								visitor with no click ID appears only in the second half, so the drop across
								the <em>Checkout Started → Registered</em> boundary is indicative rather than exact.
							</p>
							<p>
								Stages are not strictly sequential — checkout can be reached without
								submitting the visualizer, and the pricing entry never loads it — so a step
								can show{' '}
								<span style={{ color: GAIN, fontWeight: 600 }}>over 100% retention</span>{' '}
								where more people arrive than came from the step before. Filter to a single
								entry point to read a clean sequence.
							</p>
							<p className="mb-0">
								Pixel counts undercount purchases: <code>fbclids</code> has a
								UNIQUE(fbclid, event) constraint that collapses repeat events on one click
								ID, and the Apple Pay path never calls <code>/pixel</code>. The database
								stages have neither problem.
							</p>
						</div>
					</>
				)}
				</>
				)}
			</Container>
		</div>
	);
}

export default Cro;
